/**
 * rcFilters — the RC row filter matrix + row builder from Angular
 * `OrderCopyDetailsComponent.getRcVal()` / `addRcLoop()`.
 *
 * Decides which RC rows to show for a counselling based on role + location, and
 * builds each row's shape. Ported verbatim — the branch order and comparisons
 * decide who sees which district/state RC entry.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);
const num = (v: any): number => Number(v);

/** The role context this page branches on (Angular session tokens). */
export interface RcRoleContext {
  /** Angular: Tkn_District (userSessionService.userId()) */
  district: number;
  /** Angular: Tkn_UserType */
  userType: string;
  /** Angular: Tkn_UserType1 */
  userType1: string;
}

/** A single RC form row (Angular addRcLoop group). */
export interface RcRow {
  DistrictName: any;
  RcNo: any;
  OtherNme: any;
  Sign: any;
  SignatureKey: any;
  SignatureURL: string;
  DistrictId: any;
  IndexId: any;
  UserId: any;
}

/**
 * Compute the RcFlag / `loc` value passed to getRc and postSingleRc.
 * (Angular getRcVal / postRC loc logic.)
 */
export function computeRcFlag(
  counsellingDetails: any,
  role: RcRoleContext
): any {
  const { district, userType, userType1 } = role;
  const loc = str(counsellingDetails?.Location);
  if (
    district === 16 &&
    userType === '10' &&
    userType1 === '2' &&
    loc === '1'
  ) {
    return '2';
  }
  if (
    userType === '10' &&
    userType1 === '3' &&
    str(counsellingDetails?.schType) === '2' &&
    loc === '4'
  ) {
    return '2';
  }
  return counsellingDetails?.Location;
}

/** Angular addRcLoop: resolve the DistrictName column per location/role. */
function resolveDistrictName(
  val: any,
  counsellingDetails: any,
  role: RcRoleContext
): any {
  const { district, userType, userType1 } = role;
  const loc = str(counsellingDetails?.Location);

  if (loc === '3' || loc === '5') return val.state_name;
  if (
    loc === '1' &&
    district !== 16 &&
    userType === '10' &&
    userType1 === '2'
  ) {
    return val.block_name;
  }
  if (
    loc === '1' &&
    district === 16 &&
    userType === '10' &&
    userType1 === '2'
  ) {
    return val.DisNme;
  }
  if (loc === '2') return val.DisNme;
  if (loc === '4') {
    return str(counsellingDetails?.schType) === '2' &&
      userType === '10' &&
      userType1 === '3'
      ? val.DisNme
      : val.edu_dist_name;
  }
  return val.block_name;
}

/** Angular addRcLoop: build a single RC row FormGroup value. */
export function buildRcRow(
  val: any,
  counsellingDetails: any,
  role: RcRoleContext
): RcRow {
  return {
    DistrictName: resolveDistrictName(val, counsellingDetails, role),
    RcNo: val.RcNo,
    OtherNme: val.OtherNme,
    Sign: val.signature,
    SignatureKey: val.signature,
    SignatureURL: '',
    DistrictId: val.District,
    IndexId: val.IndexId,
    UserId: val.id,
  };
}

/**
 * Angular getRcVal filter matrix: decide whether a given RC record should be
 * added as a row, given the role + counselling details. Ported verbatim.
 */
export function shouldAddRc(
  val: any,
  counsellingDetails: any,
  role: RcRoleContext
): boolean {
  const { district, userType1 } = role;
  // Plain string alias avoids TS narrowing `userType` to a literal union across
  // the `||`/`&&` chain (the Angular condition mixes 9/22 and 10 checks).
  const ut: string = role.userType;
  const loc = str(counsellingDetails?.Location);
  const schType = str(counsellingDetails?.schType);

  // Angular mixes (9||22) with a nested `10` check that can never be true —
  // kept verbatim; String(ut) stops TS narrowing the dead sub-branch.
  if (
    ((ut === '9' || ut === '22') &&
      num(val.District) === district &&
      (loc === '2' ||
        (district === 16 &&
          String(ut) === '10' &&
          userType1 === '2' &&
          loc === '1'))) ||
    (ut === '9' && loc === '3')
  ) {
    return true;
  }
  if (ut !== '9' && loc === '3') {
    return true;
  }
  if (ut === '5' && userType1 !== '29') {
    return true;
  }
  if (
    ut === '10' &&
    num(val.edu_dist_id) === district &&
    (loc === '1' || loc === '4')
  ) {
    return true;
  }
  if (ut === '10' && userType1 === '2' && district === 16) {
    return str(val.District) === '28';
  }
  if (ut === '5' && userType1 === '29') {
    return str(val.District) === '29';
  }
  if (ut === '10' && loc === '5') {
    return true;
  }
  if (
    ut === '9' &&
    schType === '2' &&
    loc === '4' &&
    num(val.District) === district
  ) {
    return true;
  }
  return false;
}

/* eslint-enable @typescript-eslint/no-explicit-any */
